<template>
  <div>
    <!-- Modal Backdrop -->
    <div v-if="modelValue" class="modal-backdrop fade show"></div>
    
    <!-- Reset Password Modal -->
    <div 
      v-if="modelValue" 
      class="modal fade show" 
      id="resetPasswordModal" 
      tabindex="-1" 
      aria-labelledby="resetPasswordModalLabel" 
      style="display: block;" 
      aria-modal="true" 
      role="dialog"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="resetPasswordModalLabel">{{ translate('auth.reset_password', 'Reset Password') }}</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="successMessage" class="alert alert-success mb-3">
              {{ successMessage }}
            </div>
            
            <div v-if="error" class="alert alert-danger mb-3">
              {{ error }}
            </div>
            
            <!-- diferent message depending on if it is authenticated user or not -->
            <p v-if="!defaultEmail">{{ translate('auth.reset_password_instructions', 'Enter your email address and we will send you a link to reset your password.') }}</p>
            
            <p v-else>{{ translate('auth.reset_password_authenticated', 'We will send a password reset link to your email address.') }}</p>
            
            <form @submit.prevent="handleSubmit">
              <!-- Show input field only if there is no default email (LoginView) -->
              <div v-if="!defaultEmail" class="mb-3">
                <label class="form-label">{{ translate('auth.email', 'Email Address') }}</label>
                <input 
                  type="email" 
                  class="form-control" 
                  v-model="email"
                  :placeholder="translate('auth.email_placeholder', 'Enter your email address')"
                  required
                  :disabled="loading || successMessage"
                  autocomplete="email"
                >
              </div>
              
              <!-- Show email if there is a default email (HomeView) -->
              <div v-else class="mb-3 text-center">
                <div class="email-display p-2 border rounded bg-light">
                  <strong>{{ email }}</strong>
                </div>
              </div>
              
              <div class="d-grid gap-2">
                <!--Buttons to send and cancel (visible only before sending) -->
                <template v-if="!successMessage">
                  <button 
                    type="submit" 
                    class="btn btn-primary" 
                    :disabled="loading"
                  >
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    {{ translate('auth.send_reset_link', 'Send Reset Link') }}
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-secondary" 
                    @click="closeModal"
                  >
                    {{ translate('auth.cancel', 'Cancel') }}
                  </button>
                </template>
                
                <!-- Button to accept (visible only after successful sending) -->
                <template v-else>
                  <button 
                    type="button" 
                    class="btn btn-primary" 
                    @click="acceptAndClose"
                  >
                    {{ translate('auth.accept', 'Accept') }}
                  </button>
                </template>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { auth } from '@/config/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

export default {
  name: 'ResetPasswordModal',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    
    // Determines if it should close the session after sending the email
    logoutAfterSend: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'logout'],
  setup(props, { emit }) {
    const store = useStore();
    const email = ref('');
    const loading = ref(false);
    const error = ref(null);
    const successMessage = ref(null);
    
    // Translation function
    const translate = (key, fallback) => store.getters.translate(key, fallback);
    
    const handleSubmit = async () => {
      error.value = null;
      successMessage.value = null;
      
      if (!email.value) {
        error.value = translate('auth.email_required', 'Please enter your email address.');
        return;
      }
      
      loading.value = true;
      
      try {
        await sendPasswordResetEmail(auth, email.value);
        // Construir el mensaje con el email completo
        successMessage.value = translate('auth.reset_email_sent', 'Password reset email sent to') + 
                              ` "${email.value}"` + 
                              `. ` + 
                              translate('auth.reset_email_sent_2', 'Please check your inbox and follow the instructions.')
        
      } catch (err) {
        error.value = err.message || translate('auth.reset_email_error', 'Failed to send reset email. Please try again.');
        console.error('Error sending password reset email:', err);
      } finally {
        loading.value = false;
      }
    };
    
    const closeModal = () => {
      emit('update:modelValue', false);
      
      // If password reset email was sent successfully and we're in HomeView (logoutAfterSend is true),
      // also trigger logout when closing the modal
      if (successMessage.value && props.logoutAfterSend) {
        emit('logout');
      }
      
      // Reset states only if there was no success or we are not closing the session
      if (!successMessage.value || !props.logoutAfterSend) {
        error.value = null;
        if (!props.defaultEmail) {
          email.value = '';
        }
      }
    };
    
    const acceptAndClose = () => {
      // If it should close the session (HomeView case)
      if (props.logoutAfterSend) {
        emit('logout');
      }
      // In both cases we close the modal
      closeModal();
    };
    
    return {
      email,
      loading,
      error,
      successMessage,
      translate,
      handleSubmit,
      closeModal,
      acceptAndClose
    };
  }
};
</script>

<style scoped>

p {
  font-size: 1rem;
}
</style>

